import { validateEnumTranslations } from '../../safety_checks';
import { SeoRuleKey, SeoRuleStatus, SeoScoreGrade, SeoScoreGroup } from '../seo/content-score/types';

/**
 * Libellés et conseils du score SEO de contenu (spec
 * `docs/to_implement/seo/seo-content-score-spec.md`). Une règle = un libellé
 * court (`SEO_RULE_<KEY>`) + un conseil actionnable (`SEO_RULE_HINT_<KEY>`).
 * Affichés dans l'éditeur d'article pro, l'admin Payload et le script d'éval.
 */

export const SEO_SCORE_GROUP_TRANSLATE_KEY = 'SEO_SCORE_GROUP_';
export const SEO_SCORE_GRADE_TRANSLATE_KEY = 'SEO_SCORE_GRADE_';
export const SEO_RULE_STATUS_TRANSLATE_KEY = 'SEO_RULE_STATUS_';
export const SEO_RULE_TRANSLATE_KEY = 'SEO_RULE_';
export const SEO_RULE_HINT_TRANSLATE_KEY = 'SEO_RULE_HINT_';

export const SeoScoreGroupTranslation = {
  'fr-FR': {
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.KEYWORD}`]: 'Ciblage du mot-clé',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.STRUCTURE}`]: 'Structure',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.LENGTH_META}`]: 'Longueur et métadonnées',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.READABILITY}`]: 'Lisibilité',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.LINKS}`]: 'Liens',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.IMAGES}`]: 'Images',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.STYLE}`]: 'Style et naturel',
    [`${SEO_SCORE_GROUP_TRANSLATE_KEY}${SeoScoreGroup.BRIEF}`]: 'Respect du brief',
  },
};

export const SeoScoreGradeTranslation = {
  'fr-FR': {
    [`${SEO_SCORE_GRADE_TRANSLATE_KEY}${SeoScoreGrade.EXCELLENT}`]: 'Excellent',
    [`${SEO_SCORE_GRADE_TRANSLATE_KEY}${SeoScoreGrade.GOOD}`]: 'Bon',
    [`${SEO_SCORE_GRADE_TRANSLATE_KEY}${SeoScoreGrade.NEEDS_WORK}`]: 'À retravailler',
    [`${SEO_SCORE_GRADE_TRANSLATE_KEY}${SeoScoreGrade.POOR}`]: 'Insuffisant',
  },
};

export const SeoRuleStatusTranslation = {
  'fr-FR': {
    [`${SEO_RULE_STATUS_TRANSLATE_KEY}${SeoRuleStatus.PASS}`]: 'Ok',
    [`${SEO_RULE_STATUS_TRANSLATE_KEY}${SeoRuleStatus.WARN}`]: 'À améliorer',
    [`${SEO_RULE_STATUS_TRANSLATE_KEY}${SeoRuleStatus.FAIL}`]: 'À corriger',
    [`${SEO_RULE_STATUS_TRANSLATE_KEY}${SeoRuleStatus.SKIPPED}`]: 'Non évalué',
  },
};

const K = SEO_RULE_TRANSLATE_KEY;
const H = SEO_RULE_HINT_TRANSLATE_KEY;

export const SeoRuleTranslation = {
  'fr-FR': {
    // A. Ciblage
    [`${K}${SeoRuleKey.KW_IN_TITLE}`]: 'Mot-clé dans le titre',
    [`${H}${SeoRuleKey.KW_IN_TITLE}`]: 'Placez le mot-clé cible dans le titre, idéalement dans sa première moitié.',
    [`${K}${SeoRuleKey.KW_IN_META_TITLE}`]: 'Mot-clé dans le meta title',
    [`${H}${SeoRuleKey.KW_IN_META_TITLE}`]: 'Le meta title doit contenir le mot-clé, de préférence au début.',
    [`${K}${SeoRuleKey.KW_IN_META_DESC}`]: 'Mot-clé dans la meta description',
    [`${H}${SeoRuleKey.KW_IN_META_DESC}`]:
      'Reprenez l’idée du mot-clé dans la meta description (une reformulation naturelle suffit) : elle est mise en gras dans les résultats.',
    [`${K}${SeoRuleKey.KW_IN_SLUG}`]: 'Mot-clé dans l’URL',
    [`${H}${SeoRuleKey.KW_IN_SLUG}`]: 'Le slug doit contenir les mots principaux du mot-clé.',
    [`${K}${SeoRuleKey.KW_IN_INTRO}`]: 'Mot-clé dans l’introduction',
    [`${H}${SeoRuleKey.KW_IN_INTRO}`]: 'Le mot-clé doit apparaître naturellement dans les 100 premiers mots.',
    [`${K}${SeoRuleKey.KW_IN_HEADINGS}`]: 'Mot-clé dans les intertitres',
    [`${H}${SeoRuleKey.KW_IN_HEADINGS}`]:
      'Un ou deux intertitres peuvent reprendre le mot-clé ou une variante proche ; jamais tous, et jamais au prix d’un titre artificiel.',
    [`${K}${SeoRuleKey.KW_DENSITY}`]: 'Densité du mot-clé',
    [`${H}${SeoRuleKey.KW_DENSITY}`]:
      'Entre 0,3 % et 1,8 % : reformulez la requête en français naturel, variez avec les mots-clés secondaires ; au-delà, c’est du bourrage.',
    [`${K}${SeoRuleKey.KW_IN_IMAGE_ALT}`]: 'Mot-clé dans un texte alternatif',
    [`${H}${SeoRuleKey.KW_IN_IMAGE_ALT}`]:
      'Une image (pas toutes) doit avoir un alt qui décrit la scène et contient le mot-clé.',
    [`${K}${SeoRuleKey.KW_STUFFING}`]: 'Bourrage de mot-clé',
    [`${H}${SeoRuleKey.KW_STUFFING}`]:
      'Le mot-clé exact revient trop souvent : variez les formulations et les synonymes.',
    [`${K}${SeoRuleKey.SECONDARY_KW_COVERAGE}`]: 'Mots-clés secondaires',
    [`${H}${SeoRuleKey.SECONDARY_KW_COVERAGE}`]: 'Intégrez les mots-clés secondaires manquants là où ils ont du sens.',
    // B. Structure
    [`${K}${SeoRuleKey.SINGLE_H1}`]: 'Un seul titre principal',
    [`${H}${SeoRuleKey.SINGLE_H1}`]:
      'L’article doit avoir exactement un H1 : le titre. Les autres titres passent en H2/H3.',
    [`${K}${SeoRuleKey.HEADING_HIERARCHY}`]: 'Hiérarchie des titres',
    [`${H}${SeoRuleKey.HEADING_HIERARCHY}`]:
      'Ne sautez pas de niveau : un H3 suit un H2, pas un H1 ni un H4 directement.',
    [`${K}${SeoRuleKey.H2_RATIO}`]: 'Nombre d’intertitres',
    [`${H}${SeoRuleKey.H2_RATIO}`]: 'Comptez un H2 pour 200 à 400 mots pour aérer la lecture et structurer le sujet.',
    [`${K}${SeoRuleKey.HEADING_LENGTH}`]: 'Longueur des intertitres',
    [`${H}${SeoRuleKey.HEADING_LENGTH}`]: 'Gardez les intertitres sous 70 caractères.',
    [`${K}${SeoRuleKey.EMPTY_SECTION}`]: 'Sections vides',
    [`${H}${SeoRuleKey.EMPTY_SECTION}`]: 'Chaque intertitre doit être suivi d’au moins un paragraphe.',
    [`${K}${SeoRuleKey.INTRO_PRESENT}`]: 'Introduction',
    [`${H}${SeoRuleKey.INTRO_PRESENT}`]: 'Ouvrez par un ou deux paragraphes avant le premier intertitre.',
    [`${K}${SeoRuleKey.PARAGRAPH_LENGTH}`]: 'Longueur des paragraphes',
    [`${H}${SeoRuleKey.PARAGRAPH_LENGTH}`]: 'Coupez les paragraphes de plus de 120 mots.',
    [`${K}${SeoRuleKey.PARAGRAPH_VARIETY}`]: 'Rythme des paragraphes',
    [`${H}${SeoRuleKey.PARAGRAPH_VARIETY}`]:
      'Évitez les suites de paragraphes d’une seule phrase : regroupez les idées.',
    [`${K}${SeoRuleKey.PARAGRAPH_UNIFORMITY}`]: 'Paragraphes tous pareils',
    [`${H}${SeoRuleKey.PARAGRAPH_UNIFORMITY}`]:
      'Toutes les sections ont la même forme et les paragraphes la même taille : fusionnez ou scindez des paragraphes, gardez une section courte, laissez une phrase seule quand elle le mérite.',
    [`${K}${SeoRuleKey.LIST_OR_TABLE_SANITY}`]: 'Listes et tableaux',
    [`${H}${SeoRuleKey.LIST_OR_TABLE_SANITY}`]:
      'Pas de liste à un seul élément, pas de tableau sans en-tête, et du texte courant majoritaire.',
    [`${K}${SeoRuleKey.FAQ_PRESENT}`]: 'FAQ',
    [`${H}${SeoRuleKey.FAQ_PRESENT}`]: 'Une FAQ de 3 à 5 questions réelles aide au référencement (bonus).',
    // C. Longueur / métas
    [`${K}${SeoRuleKey.WORD_COUNT}`]: 'Longueur de l’article',
    [`${H}${SeoRuleKey.WORD_COUNT}`]: 'Ajustez la longueur à la cible : ni trop court pour être utile, ni délayé.',
    [`${K}${SeoRuleKey.META_TITLE_LENGTH}`]: 'Longueur du meta title',
    [`${H}${SeoRuleKey.META_TITLE_LENGTH}`]: 'Entre 30 et 60 caractères pour ne pas être tronqué dans les résultats.',
    [`${K}${SeoRuleKey.META_DESC_LENGTH}`]: 'Longueur de la meta description',
    [`${H}${SeoRuleKey.META_DESC_LENGTH}`]: 'Entre 70 et 155 caractères, avec le mot-clé et une raison de cliquer.',
    [`${K}${SeoRuleKey.META_TITLE_UNIQUE}`]: 'Meta title distinct du titre',
    [`${H}${SeoRuleKey.META_TITLE_UNIQUE}`]:
      'Un meta title différent du H1 permet de viser une formulation supplémentaire (bonus).',
    [`${K}${SeoRuleKey.SLUG_QUALITY}`]: 'Qualité de l’URL',
    [`${H}${SeoRuleKey.SLUG_QUALITY}`]: 'Slug court, en minuscules sans accent, sans mot vide ni année.',
    [`${K}${SeoRuleKey.EXCERPT_PRESENT}`]: 'Extrait',
    [`${H}${SeoRuleKey.EXCERPT_PRESENT}`]: 'Rédigez un extrait de 80 à 300 caractères pour les listes et le partage.',
    // D. Lisibilité
    [`${K}${SeoRuleKey.SENTENCE_LENGTH_AVG}`]: 'Longueur moyenne des phrases',
    [`${H}${SeoRuleKey.SENTENCE_LENGTH_AVG}`]: 'Visez 20 mots par phrase en moyenne.',
    [`${K}${SeoRuleKey.LONG_SENTENCES}`]: 'Phrases longues',
    [`${H}${SeoRuleKey.LONG_SENTENCES}`]: 'Coupez les phrases de plus de 30 mots.',
    [`${K}${SeoRuleKey.READABILITY_FR}`]: 'Indice de lisibilité',
    [`${H}${SeoRuleKey.READABILITY_FR}`]:
      'Des phrases plus courtes et des mots plus simples font monter l’indice ; un texte simple n’est jamais pénalisé.',
    [`${K}${SeoRuleKey.PASSIVE_VOICE}`]: 'Voix passive',
    [`${H}${SeoRuleKey.PASSIVE_VOICE}`]:
      'Préférez la voix active : « le coiffeur réalise » plutôt que « est réalisé par ».',
    [`${K}${SeoRuleKey.TRANSITION_WORDS}`]: 'Connecteurs en excès',
    [`${H}${SeoRuleKey.TRANSITION_WORDS}`]:
      'Trop de phrases portées par un connecteur (en effet, de plus, ainsi…) : c’est la signature d’un texte généré. Enchaînez les idées par le sens, pas par des liaisons.',
    [`${K}${SeoRuleKey.SAME_SENTENCE_START}`]: 'Débuts de phrase répétés',
    [`${H}${SeoRuleKey.SAME_SENTENCE_START}`]: 'Variez les débuts de phrases consécutives.',
    [`${K}${SeoRuleKey.NOMINAL_STACKS}`]: 'Empilements de compléments',
    [`${H}${SeoRuleKey.NOMINAL_STACKS}`]: 'Allégez les enchaînements « de … de … de » (bonus).',
    // E. Liens
    [`${K}${SeoRuleKey.INTERNAL_LINKS_COUNT}`]: 'Liens internes',
    [`${H}${SeoRuleKey.INTERNAL_LINKS_COUNT}`]:
      'Ajoutez des liens vers vos pages et articles en rapport, sans en abuser.',
    [`${K}${SeoRuleKey.LINK_DISTRIBUTION}`]: 'Répartition des liens',
    [`${H}${SeoRuleKey.LINK_DISTRIBUTION}`]:
      'Répartissez les liens dans le corps de l’article, pas seulement à la fin.',
    [`${K}${SeoRuleKey.ANCHOR_QUALITY}`]: 'Texte des liens',
    [`${H}${SeoRuleKey.ANCHOR_QUALITY}`]:
      'Le texte du lien doit décrire la page cible : évitez « ici », « cliquez » ou une URL brute.',
    [`${K}${SeoRuleKey.DUPLICATE_TARGET}`]: 'Liens en double',
    [`${H}${SeoRuleKey.DUPLICATE_TARGET}`]: 'Un seul lien par page cible suffit.',
    [`${K}${SeoRuleKey.LINK_DENSITY}`]: 'Densité de liens',
    [`${H}${SeoRuleKey.LINK_DENSITY}`]: 'Pas plus d’un lien pour 100 mots.',
    [`${K}${SeoRuleKey.CONVERSION_LINK}`]: 'Lien vers une page de conversion',
    [`${H}${SeoRuleKey.CONVERSION_LINK}`]: 'Reliez l’article à une page de réservation ou de prestation.',
    [`${K}${SeoRuleKey.EXTERNAL_SOURCE}`]: 'Source externe',
    [`${H}${SeoRuleKey.EXTERNAL_SOURCE}`]:
      'Un chiffre venu d’une page externe se publie avec un lien vers sa source (bonus, évalué seulement quand le brief en contient).',
    [`${K}${SeoRuleKey.FORBIDDEN_LINK}`]: 'Lien interdit',
    [`${H}${SeoRuleKey.FORBIDDEN_LINK}`]: 'Retirez les liens vers les domaines interdits (concurrents…).',
    // F. Images
    [`${K}${SeoRuleKey.IMAGE_PRESENT}`]: 'Images',
    [`${H}${SeoRuleKey.IMAGE_PRESENT}`]:
      'Ajoutez une image d’illustration : elle sert de visuel à l’article et à son partage.',
    [`${K}${SeoRuleKey.IMAGE_ALT}`]: 'Textes alternatifs',
    [`${H}${SeoRuleKey.IMAGE_ALT}`]: 'Chaque image a besoin d’un alt qui décrit ce qu’on voit, en 5 à 125 caractères.',
    [`${K}${SeoRuleKey.IMAGE_FILENAME}`]: 'Noms de fichiers',
    [`${H}${SeoRuleKey.IMAGE_FILENAME}`]:
      'Nommez les fichiers avec des mots (« coupe-femme-lyon.webp »), pas « IMG_2034 ».',
    [`${K}${SeoRuleKey.IMAGE_WEIGHT}`]: 'Poids des images',
    [`${H}${SeoRuleKey.IMAGE_WEIGHT}`]: 'Compressez les images : moins de 200 Ko et 1 600 px de large.',
    // G. Style
    [`${K}${SeoRuleKey.EM_DASH}`]: 'Tirets cadratins',
    [`${H}${SeoRuleKey.EM_DASH}`]:
      'Remplacez les tirets cadratins par une virgule, deux points ou une nouvelle phrase.',
    [`${K}${SeoRuleKey.AI_PHRASES}`]: 'Formules creuses',
    [`${H}${SeoRuleKey.AI_PHRASES}`]:
      'Supprimez les tournures automatiques (« dans cet article », « il est important de noter »…).',
    [`${K}${SeoRuleKey.ANGLICISMS}`]: 'Anglicismes',
    [`${H}${SeoRuleKey.ANGLICISMS}`]: 'Préférez le mot français quand il existe.',
    [`${K}${SeoRuleKey.EXCLAMATIONS}`]: 'Points d’exclamation',
    [`${H}${SeoRuleKey.EXCLAMATIONS}`]: 'Deux points d’exclamation par article suffisent.',
    [`${K}${SeoRuleKey.RHETORICAL_QUESTIONS}`]: 'Questions rhétoriques',
    [`${H}${SeoRuleKey.RHETORICAL_QUESTIONS}`]: 'Limitez les questions posées au lecteur hors FAQ.',
    [`${K}${SeoRuleKey.NGRAM_REPETITION}`]: 'Répétitions',
    [`${H}${SeoRuleKey.NGRAM_REPETITION}`]: 'Une même suite de mots revient plusieurs fois : reformulez.',
    [`${K}${SeoRuleKey.LEXICAL_DIVERSITY}`]: 'Richesse du vocabulaire',
    [`${H}${SeoRuleKey.LEXICAL_DIVERSITY}`]: 'Variez le vocabulaire : synonymes, exemples concrets.',
    [`${K}${SeoRuleKey.TRIPLET_OVERUSE}`]: 'Énumérations en trois',
    [`${H}${SeoRuleKey.TRIPLET_OVERUSE}`]:
      'Les listes de trois éléments systématiques trahissent un texte automatique.',
    [`${K}${SeoRuleKey.FORBIDDEN_TERMS}`]: 'Termes interdits',
    [`${H}${SeoRuleKey.FORBIDDEN_TERMS}`]:
      'Retirez les termes interdits (concurrents, allégations, vocabulaire réservé).',
    [`${K}${SeoRuleKey.CONTACT_IN_BODY}`]: 'Coordonnées dans le texte',
    [`${H}${SeoRuleKey.CONTACT_IN_BODY}`]:
      'Les numéros et e-mails ont leur place sur la page contact, pas dans l’article.',
    [`${K}${SeoRuleKey.CTA_COUNT}`]: 'Appels à l’action',
    [`${H}${SeoRuleKey.CTA_COUNT}`]:
      'Un ou deux appels à l’action (« Réservez », « Vous pouvez réserver en ligne ») dans tout l’article, FAQ comprise : ni aucun, ni trois ou plus.',
    [`${K}${SeoRuleKey.REASONING_LEAK}`]: 'Raisonnement déversé',
    [`${H}${SeoRuleKey.REASONING_LEAK}`]:
      'Le texte contient des traces de raisonnement ou de consignes (« je dois respecter… ») : regénérez l’article.',
    [`${K}${SeoRuleKey.SOURCE_LEAK}`]: 'Sources internes citées',
    [`${H}${SeoRuleKey.SOURCE_LEAK}`]:
      'Énoncez le fait sans citer sa provenance interne (« d’après notre grille tarifaire »).',
    [`${K}${SeoRuleKey.ENUMERATIVE_SCAFFOLDING}`]: 'Transitions mécaniques',
    [`${H}${SeoRuleKey.ENUMERATIVE_SCAFFOLDING}`]:
      'Les phrases qui s’ouvrent par « Le premier… Le second… » ou « D’abord… Ensuite… Enfin » déroulent une liste : enchaînez par le sens ou faites une vraie liste.',
    [`${K}${SeoRuleKey.META_INTRO}`]: 'Introduction qui annonce',
    [`${H}${SeoRuleKey.META_INTRO}`]:
      'L’introduction annonce l’article (« nous détaillons ici », « l’objectif est de ») au lieu d’y entrer : ouvrez sur la réponse ou sur une situation concrète.',
    // H. Brief
    [`${K}${SeoRuleKey.OUTLINE_COVERAGE}`]: 'Plan du brief',
    [`${H}${SeoRuleKey.OUTLINE_COVERAGE}`]: 'Des sections prévues par le brief manquent.',
    [`${K}${SeoRuleKey.PAA_COVERAGE}`]: 'Questions des internautes',
    [`${H}${SeoRuleKey.PAA_COVERAGE}`]: 'Répondez aux questions listées dans le brief, dans un intertitre ou la FAQ.',
    [`${K}${SeoRuleKey.ENTITY_COVERAGE}`]: 'Champ sémantique',
    [`${H}${SeoRuleKey.ENTITY_COVERAGE}`]: 'Employez les termes du champ sémantique relevés dans le brief.',
    [`${K}${SeoRuleKey.FACT_TRACE}`]: 'Chiffres tracés',
    [`${H}${SeoRuleKey.FACT_TRACE}`]:
      'Chaque chiffre doit venir d’un fait du brief : sinon il est probablement inventé.',
    [`${K}${SeoRuleKey.LENGTH_VS_SERP}`]: 'Longueur vs pages qui rankent',
    [`${H}${SeoRuleKey.LENGTH_VS_SERP}`]:
      'Repère indicatif (bonus) : entre 0,9 et 1,3 fois la médiane des pages qui rankent. Un article concis et complet vaut mieux qu’un article délayé.',
    [`${K}${SeoRuleKey.REQUIRED_LINKS}`]: 'Liens du brief',
    [`${H}${SeoRuleKey.REQUIRED_LINKS}`]:
      'Chaque lien interne prévu par le brief doit être un vrai lien, pas une ancre en texte nu.',
    [`${K}${SeoRuleKey.CTA_LINK}`]: 'Appel à l’action lié',
    [`${H}${SeoRuleKey.CTA_LINK}`]: 'L’appel à l’action doit pointer vers l’URL prévue par le brief.',
  },
};

validateEnumTranslations(SeoScoreGroup, SeoScoreGroupTranslation, SEO_SCORE_GROUP_TRANSLATE_KEY, true);
validateEnumTranslations(SeoScoreGrade, SeoScoreGradeTranslation, SEO_SCORE_GRADE_TRANSLATE_KEY, true);
validateEnumTranslations(SeoRuleStatus, SeoRuleStatusTranslation, SEO_RULE_STATUS_TRANSLATE_KEY, true);
validateEnumTranslations(SeoRuleKey, SeoRuleTranslation, SEO_RULE_TRANSLATE_KEY, true);
validateEnumTranslations(SeoRuleKey, SeoRuleTranslation, SEO_RULE_HINT_TRANSLATE_KEY, true);
